import React from 'react';
import { motion } from 'framer-motion';
import { HiPlay, HiCalendar, HiOfficeBuilding, HiTag, HiExternalLink } from 'react-icons/hi';

const volunteering = {
  title: 'Code to Inspire (CTI) - YouTube Video Content Creator (Pashto)',
  organization: 'Code to Inspire',
  type: 'Volunteering · Education',
  duration: 'October 2025 - Present',
  responsibilities: [
    'Created educational video content in Pashto for YouTube and digital learning platforms, making technical and awareness-based topics easier to understand.',
    'Developed and recorded Pashto-language video lessons for educational YouTube materials.',
    'Designed clear, engaging scripts to explain learning concepts in an accessible way.',
    'Collaborated with CTI media and education teams to align each video with learning objectives.',
    'Maintained clarity, consistency, and cultural relevance across all delivered content.',
    'Helped improve access to digital education for Pashto-speaking learners.',
  ],
  skills: [
    'Content Creation',
    'Video Script Writing',
    'Educational Content',
    'Pashto Communication',
    'Digital Media',
  ],
  videoLink: 'https://www.youtube.com/watch?v=Umqm7N2jLis&list=PLxOQSQ5FNK94bH4WzGfooY08iNmF9CT2Y&index=30',
  highlight: 'Educational video content created in Pashto to simplify technical concepts and improve access to digital learning for local students.',
};

const Volunteering = () => {
  return (
    <section id="volunteering" className="py-12 sm:py-16 md:py-20 bg-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 bg-beige-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 bg-navy-400 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-20 relative z-10">
        <motion.div
          className="text-center mb-10 sm:mb-12 md:mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-beige-600 font-semibold text-xs sm:text-sm uppercase tracking-wider">
            Community Work
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy-900 mt-2 font-primary">
            Volunteering
          </h2>
          <div className="w-16 sm:w-20 h-1 bg-beige-500 mx-auto mt-3 sm:mt-4"></div>
          <p className="mt-4 sm:mt-6 text-gray-600 max-w-2xl mx-auto text-sm sm:text-base px-4">
            Work that reflects my commitment to accessible education, community impact, and local-language learning.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-5xl mx-auto bg-gradient-to-br from-beige-50 to-white border border-beige-200 rounded-2xl shadow-xl overflow-hidden"
        >
          <div className="h-2 bg-gradient-to-r from-beige-500 to-navy-700"></div>

          <div className="p-5 sm:p-6 md:p-8">
            <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-beige-100 rounded-xl">
                    <HiPlay className="text-2xl text-beige-700" />
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-navy-900 font-primary">
                      {volunteering.title}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">{volunteering.type}</p>
                  </div>
                </div>

                <div className="space-y-2 text-gray-600 mb-5">
                  <div className="flex items-start gap-2">
                    <HiOfficeBuilding className="text-beige-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm sm:text-base">{volunteering.organization}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <HiCalendar className="text-beige-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm sm:text-base">{volunteering.duration}</span>
                  </div>
                </div>

                <ul className="space-y-2 sm:space-y-3 text-gray-700">
                  {volunteering.responsibilities.map((point, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="text-beige-500 mt-1 flex-shrink-0">▸</span>
                      <span className="text-sm sm:text-base leading-relaxed">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="lg:w-[320px] flex-shrink-0">
                <div className="bg-navy-900 rounded-2xl p-5 text-white h-full">
                  <p className="text-xs uppercase tracking-[0.2em] text-beige-300 mb-3">Featured Work</p>
                  <h4 className="text-lg sm:text-xl font-bold mb-3 font-primary">
                    ويب ديزاين | د کورس پېژندنی لوست
                  </h4>
                  <p className="text-sm text-gray-300 leading-relaxed mb-5">
                    {volunteering.highlight}
                  </p>

                  <a
                    href={volunteering.videoLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 mb-5 bg-beige-400 text-navy-900 rounded-lg font-semibold text-sm hover:bg-beige-300 transition-colors"
                  >
                    <HiExternalLink className="text-base" />
                    Watch Video
                  </a>

                  <div className="flex items-start gap-2 mb-3">
                    <HiTag className="text-beige-300 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-beige-100">Skills</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {volunteering.skills.map((skill) => (
                      <span
                        key={skill}
                        className="px-3 py-1 bg-white/10 border border-white/10 rounded-full text-xs text-gray-100"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Volunteering;
